"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.testJsonPath = testJsonPath;
exports.evaluateJsonPath = evaluateJsonPath;
exports.isJsonPath = isJsonPath;
exports.applyFieldMappingConfig = applyFieldMappingConfig;
exports.applyFullMapping = applyFullMapping;
exports.generateJsonPathSuggestions = generateJsonPathSuggestions;
const jsonpath_plus_1 = require("jsonpath-plus");
const set_1 = __importDefault(require("lodash/set"));
const json_1 = require("../../utils/json");
/**
 * Test if a JSONPath is valid against the given data
 */
function testJsonPath(props) {
    try {
        const parsed = typeof props.data === "string" ? JSON.parse(props.data) : props.data;
        (0, jsonpath_plus_1.JSONPath)({ path: props.jsonPath, json: parsed });
        return { success: true };
    }
    catch (error) {
        return {
            success: false,
            error: error instanceof Error ? error.message : "Unknown error",
        };
    }
}
/**
 * Evaluate a JSONPath against the given data and return the result
 */
function evaluateJsonPath(data, jsonPath) {
    const parsed = typeof data === "string" ? (0, json_1.parseJsonPrioritised)(data) : data;
    const result = (0, jsonpath_plus_1.JSONPath)({
        path: jsonPath,
        json: parsed,
        wrap: false,
        eval: false,
    });
    return result;
}
/**
 * Check if a value is a JSONPath (starts with $)
 */
function isJsonPath(value) {
    return value.startsWith("$");
}
/**
 * Set a value at a nested path using dot notation.
 * Creates intermediate objects as needed.
 * Uses lodash's set which has built-in prototype pollution protection.
 *
 * @example
 * setNestedValue({}, "context.user_id", "123")
 * // Returns: { context: { user_id: "123" } }
 */
function setNestedValue(obj, path, value) {
    (0, set_1.default)(obj, path, value);
}
/**
 * Apply field mapping config to get the result for a single field
 */
function applyFieldMappingConfig(props) {
    const { observation, config, defaultSourceField } = props;
    const misses = [];
    const errors = [];
    const safeEvaluate = (sourceField, jsonPath, mappingKey) => {
        try {
            return {
                success: true,
                value: evaluateJsonPath(observation[sourceField], jsonPath),
            };
        }
        catch (error) {
            errors.push({
                sourceField,
                jsonPath,
                mappingKey,
                message: error instanceof Error ? error.message : "Invalid JSONPath",
            });
            return { success: false };
        }
    };
    const withValue = (value) => ({
        value,
        misses,
        errors,
    });
    switch (config.mode) {
        case "full":
            return withValue(observation[defaultSourceField]);
        case "none":
            // null is written as Prisma.DbNull
            return withValue(null);
        case "custom": {
            if (!config.custom)
                return withValue(observation[defaultSourceField]);
            if (config.custom.type === "root") {
                const rootConfig = config.custom.rootConfig;
                if (!rootConfig)
                    return withValue(observation[defaultSourceField]);
                const evaluated = safeEvaluate(rootConfig.sourceField, rootConfig.jsonPath, null);
                if (!evaluated.success)
                    return withValue(undefined);
                if (evaluated.value === undefined) {
                    misses.push({
                        sourceField: rootConfig.sourceField,
                        jsonPath: rootConfig.jsonPath,
                        mappingKey: null,
                    });
                }
                return withValue(evaluated.value);
            }
            if (config.custom.type === "keyValueMap") {
                const keyValueMapConfig = config.custom.keyValueMapConfig;
                if (!keyValueMapConfig || keyValueMapConfig.entries.length === 0) {
                    return withValue(observation[defaultSourceField]);
                }
                const result = {};
                for (const entry of keyValueMapConfig.entries) {
                    if (!entry.value && entry.value !== "")
                        continue;
                    let resolvedValue;
                    if (isJsonPath(entry.value)) {
                        const evaluated = safeEvaluate(entry.sourceField, entry.value, entry.key);
                        if (!evaluated.success) {
                            resolvedValue = undefined;
                        }
                        else {
                            resolvedValue = evaluated.value;
                            if (resolvedValue === undefined) {
                                misses.push({
                                    sourceField: entry.sourceField,
                                    jsonPath: entry.value,
                                    mappingKey: entry.key,
                                });
                            }
                        }
                    }
                    else {
                        resolvedValue = entry.value;
                    }
                    if (entry.key.includes(".")) {
                        setNestedValue(result, entry.key, resolvedValue);
                    }
                    else {
                        result[entry.key] = resolvedValue;
                    }
                }
                return withValue(result);
            }
            return withValue(observation[defaultSourceField]);
        }
        default:
            return withValue(observation[defaultSourceField]);
    }
}
/**
 * Apply the full mapping config to an observation and return the dataset item fields
 */
function applyFullMapping(props) {
    const { observation, mapping } = props;
    const errors = [];
    const fields = [
        {
            key: "input",
            config: mapping.input,
            defaultSourceField: "input",
        },
        {
            key: "expectedOutput",
            config: mapping.expectedOutput,
            defaultSourceField: "output",
        },
        {
            key: "metadata",
            config: mapping.metadata,
            defaultSourceField: "metadata",
        },
    ];
    const results = {};
    for (const field of fields) {
        try {
            const result = applyFieldMappingConfig({
                observation,
                config: field.config,
                defaultSourceField: field.defaultSourceField,
            });
            results[field.key] = result.value;
            for (const miss of result.misses) {
                errors.push({
                    type: "json_path_miss",
                    targetField: field.key,
                    sourceField: miss.sourceField,
                    jsonPath: miss.jsonPath,
                    mappingKey: miss.mappingKey,
                    message: `JSONPath "${miss.jsonPath}" did not match any data in "${miss.sourceField}"${miss.mappingKey ? ` (key: "${miss.mappingKey}")` : ""}`,
                });
            }
            for (const err of result.errors) {
                errors.push({
                    type: "json_path_error",
                    targetField: field.key,
                    sourceField: err.sourceField,
                    jsonPath: err.jsonPath,
                    mappingKey: err.mappingKey,
                    message: `JSONPath evaluation error for "${field.key}"${err.mappingKey ? ` (key: "${err.mappingKey}")` : ""}: ${err.message}`,
                });
            }
        }
        catch (error) {
            // Isolate per-field faults so a throw from one mapping doesn't abort
            // the remaining fields on the same observation.
            errors.push({
                type: "json_path_error",
                targetField: field.key,
                sourceField: field.defaultSourceField,
                jsonPath: "",
                mappingKey: null,
                message: `JSONPath evaluation error for "${field.key}": ${error instanceof Error ? error.message : "Unknown error"}`,
            });
            results[field.key] = undefined;
        }
    }
    return {
        input: results.input,
        expectedOutput: results.expectedOutput,
        metadata: results.metadata,
        errors,
    };
}
/**
 * Generate autocomplete suggestions for JSONPaths based on the data structure
 */
function generateJsonPathSuggestions(data, prefix = "$") {
    const suggestions = [];
    if (data === null || data === undefined) {
        return suggestions;
    }
    if (typeof data === "object") {
        if (Array.isArray(data)) {
            // For arrays, suggest index access and wildcard
            suggestions.push(`${prefix}[0]`);
            suggestions.push(`${prefix}[*]`);
            // Also recurse into first element
            if (data.length > 0) {
                suggestions.push(...generateJsonPathSuggestions(data[0], `${prefix}[0]`));
            }
        }
        else {
            // For objects, suggest each key
            for (const [key, value] of Object.entries(data)) {
                const childPath = `${prefix}.${key}`;
                suggestions.push(childPath);
                // Recurse into nested objects/arrays
                suggestions.push(...generateJsonPathSuggestions(value, childPath));
            }
        }
    }
    return suggestions;
}
